import type { ReactNode } from 'react'
import type { JournalPost } from '@/types'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'

import { useGsap } from '@/hooks/use-gsap'
import { SplitWords } from '@/components/generals/split-words'
import { JournalCard } from '@/components/journal/journal-card'

interface JournalPreviewProps {
    posts?: JournalPost[]
}

/**
 * The strip is purely presentational and already renders nothing when there is
 * no journal, so an absent list is a defined state rather than a failure.
 */
export const JournalPreview = ({ posts = [] }: JournalPreviewProps): ReactNode => {
    const scope = useGsap<HTMLElement>(({ gsap, select, scope: root, reduced }) => {
        if (reduced) return

        gsap.from(select('[data-journal-heading] .reveal-mask > span'), {
            yPercent: 115,
            duration: 1,
            stagger: 0.05,
            ease: 'nuvea',
            scrollTrigger: { trigger: root, start: 'top 78%' }
        })

        gsap.from(select('[data-journal-link]'), {
            y: 18,
            opacity: 0,
            duration: 0.8,
            delay: 0.12,
            ease: 'nuvea',
            scrollTrigger: { trigger: root, start: 'top 78%' }
        })

        // Cards rise in sequence, the lead card slightly ahead of the pair.
        gsap.from(select('[data-journal-card]'), {
            y: 56,
            opacity: 0,
            duration: 1,
            stagger: 0.1,
            ease: 'nuvea',
            scrollTrigger: {
                trigger: select('[data-journal-grid]')[0],
                start: 'top 85%'
            }
        })
    }, [posts.length])

    if (posts.length === 0) return null

    const [lead, ...rest] = posts

    return (
        <section
            ref={scope}
            id='journal'
            className='shell-x mx-auto w-full max-w-[1760px] pb-24 md:pb-32'
        >
            <div className='flex flex-col gap-6 md:flex-row md:items-end md:justify-between'>
                <SplitWords
                    data-journal-heading=''
                    as='h2'
                    lines={['From the journal.']}
                    className='type-h1 text-ink'
                />

                <Link
                    data-journal-link=''
                    href='/journal'
                    className='type-small group inline-flex w-fit items-center gap-2 rounded-full border border-stone-line px-5 py-3 text-ink transition-[background-color,border-color,color] duration-400 ease-nuvea hover:border-ink hover:bg-ink hover:text-cream'
                >
                    View All

                    <ArrowUpRight
                        className='size-4 transition-transform duration-400 ease-nuvea group-hover:translate-x-0.5 group-hover:-translate-y-0.5'
                        strokeWidth={1.6}
                    />
                </Link>
            </div>

            <div data-journal-grid='' className='mt-10 flex flex-col gap-3 md:mt-14'>
                <JournalCard post={lead} variant='featured' />

                {rest.length > 0 && (
                    <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                        {rest.slice(0, 2).map((post) => (
                            <JournalCard key={post.id} post={post} />
                        ))}
                    </div>
                )}
            </div>
        </section>
    )
}
